/**
 * Sector operations — Stays room states and pure transition table.
 * R39 K2.a + K2.b (plan docs/plans/sector-operations.md §Part K, SO-P113, SO-D201, SO-D206, SO-D207).
 */

import type { OpsRoomPmsSync } from './pms.js'

export const OPS_ROOM_OCCUPANCIES = ['VACANT', 'OCCUPIED', 'DUE_OUT'] as const
export type OpsRoomOccupancy = (typeof OPS_ROOM_OCCUPANCIES)[number]

export { workspaceTodayKey, resolveWorkspaceTimeZone, formatZonedDateKey } from '../repeat/keys.js'

export const OPS_ROOM_HK_STATES = ['DIRTY', 'CLEANING', 'CLEANED', 'READY'] as const
export type OpsRoomHk = (typeof OPS_ROOM_HK_STATES)[number]

export const OPS_ROOM_SERVICE_STATES = ['IN_SERVICE', 'OUT_OF_ORDER', 'OUT_OF_SERVICE'] as const
export type OpsRoomService = (typeof OPS_ROOM_SERVICE_STATES)[number]

export const OPS_ROOM_SOURCES = ['DESK', 'QR', 'CSV', 'BOARD', 'PMS'] as const
export type OpsRoomSource = (typeof OPS_ROOM_SOURCES)[number]

export const DEFAULT_ROOM_CLEAN_PHOTOS = [
  'Bed',
  'Bathroom',
  'Minibar and amenities',
] as const

export const OPS_ROOM_PAUSE_REASONS = ['GUEST_IN_ROOM', 'DND', 'OTHER'] as const
export type OpsRoomPauseReason = (typeof OPS_ROOM_PAUSE_REASONS)[number]

export interface OpsRoomGeoPoint {
  lat: number
  lng: number
  accuracy?: number
}

export interface OpsRoomState {
  workspaceId: string
  placeId: string
  propertyId: string
  blockId?: string | null
  sector?: 'stays'
  occupancy: OpsRoomOccupancy
  dueOutAt?: number | null
  hk: OpsRoomHk
  service: OpsRoomService
  serviceReason?: string | null
  workOrderRequestId?: string | null
  dnd: boolean
  vip: boolean
  cleanRecordId?: string | null
  attendantUid?: string | null
  assignedUid?: string | null
  assignedDay?: string | null
  startedAt?: number | null
  cleanedAt?: number | null
  inspectedBy?: string | null
  failedItems: string[]
  pausedAt: number | null
  pausedMs: number
  pauseReason: OpsRoomPauseReason | null
  startGeo: OpsRoomGeoPoint | null
  pmsSync?: OpsRoomPmsSync | null
  pmsLastEventAt?: number | null
  version: number
  updatedAt?: unknown
  updatedBy?: string
}

export function createDefaultRoomState(params: {
  workspaceId: string
  placeId: string
  propertyId: string
  blockId?: string | null
}): OpsRoomState {
  return {
    workspaceId: params.workspaceId,
    placeId: params.placeId,
    propertyId: params.propertyId,
    blockId: params.blockId ?? null,
    sector: 'stays',
    occupancy: 'VACANT',
    dueOutAt: null,
    hk: 'READY',
    service: 'IN_SERVICE',
    serviceReason: null,
    workOrderRequestId: null,
    dnd: false,
    vip: false,
    cleanRecordId: null,
    attendantUid: null,
    assignedUid: null,
    assignedDay: null,
    startedAt: null,
    cleanedAt: null,
    inspectedBy: null,
    failedItems: [],
    pausedAt: null,
    pausedMs: 0,
    pauseReason: null,
    startGeo: null,
    pmsSync: null,
    pmsLastEventAt: null,
    version: 0,
  }
}

export type OpsRoomAction =
  | { type: 'CHECK_OUT' }
  | { type: 'MARK_OCCUPIED' }
  | { type: 'SET_DUE_OUT'; dueOutAt: number }
  | { type: 'START_CLEAN'; attendantUid: string; startedAt?: number; geo?: OpsRoomGeoPoint | null }
  | { type: 'PAUSE_CLEAN'; reason: OpsRoomPauseReason; at?: number }
  | { type: 'RESUME_CLEAN'; at?: number }
  | { type: 'CLEANED'; cleanRecordId: string; cleanedAt?: number }
  | { type: 'INSPECT_PASS'; inspectedBy: string }
  | { type: 'INSPECT_FAIL'; inspectedBy: string; failedItems: string[] }
  | {
      type: 'SET_SERVICE'
      service: 'OUT_OF_ORDER' | 'OUT_OF_SERVICE'
      serviceReason: string
      workOrderRequestId?: string | null
    }
  | { type: 'BACK_IN_SERVICE' }
  | { type: 'SET_TAGS'; dnd?: boolean; vip?: boolean }
  | { type: 'ASSIGN'; uid: string; day: string }
  | { type: 'UNASSIGN' }
  | { type: 'REASSIGN'; uid: string; day: string }

export interface OpsRoomEventStateSnapshot {
  occupancy: OpsRoomOccupancy
  hk: OpsRoomHk
  service: OpsRoomService
  dnd: boolean
  vip: boolean
}

export interface OpsRoomEvent {
  id: string
  workspaceId: string
  placeId: string
  action: string
  actorUid: string
  actorName: string
  from: OpsRoomEventStateSnapshot
  to: OpsRoomEventStateSnapshot
  source: OpsRoomSource
  at: number | unknown
  metadata?: Record<string, unknown>
}

export interface TransitionRoomStateOptions {
  isFrontDeskOrAdmin?: boolean
  isSupervisor?: boolean
  clearDnd?: boolean
  now?: number
  reassignAttendantTo?: string
  todayKey?: string
  actorUid?: string
}

export interface TransitionRoomStateResult {
  nextState: OpsRoomState
  from: OpsRoomEventStateSnapshot
  to: OpsRoomEventStateSnapshot
}

/**
 * Pure transition function for room states per SO-P113:
 * - Refuses every illegal move (for example CLEANED without CLEANING,
 *   INSPECT on a non-CLEANED room, START_CLEAN while OUT_OF_ORDER,
 *   any hk move with dnd on except by front desk clearing it).
 * - Increments state version by 1.
 */
export function transitionRoomState(
  current: OpsRoomState,
  action: OpsRoomAction,
  options?: TransitionRoomStateOptions,
): TransitionRoomStateResult {
  const from: OpsRoomEventStateSnapshot = {
    occupancy: current.occupancy,
    hk: current.hk,
    service: current.service,
    dnd: current.dnd,
    vip: current.vip,
  }

  const currentPausedAt = current.pausedAt ?? null
  const currentPausedMs = typeof current.pausedMs === 'number' ? current.pausedMs : 0
  const currentPauseReason = current.pauseReason ?? null
  const currentStartGeo = current.startGeo ?? null

  const isSupervisor = Boolean(options?.isSupervisor || options?.isFrontDeskOrAdmin)

  const next: OpsRoomState = {
    ...current,
    sector: current.sector ?? 'stays',
    pausedAt: currentPausedAt,
    pausedMs: currentPausedMs,
    pauseReason: currentPauseReason,
    startGeo: currentStartGeo,
    pmsSync: current.pmsSync ?? null,
    failedItems: Array.isArray(current.failedItems) ? [...current.failedItems] : [],
    version: current.version + 1,
  }

  const isHkMove =
    action.type === 'START_CLEAN' ||
    action.type === 'PAUSE_CLEAN' ||
    action.type === 'RESUME_CLEAN' ||
    action.type === 'CLEANED' ||
    action.type === 'INSPECT_PASS' ||
    action.type === 'INSPECT_FAIL'

  // Rule: Any hk move with dnd on is blocked, except by front desk / admin clearing it.
  // Exception (SO-D205): PAUSE_CLEAN is allowed while dnd is on (e.g. guest turns on DND mid-clean),
  // leaving dnd as it is. RESUME_CLEAN remains blocked under DND unless front desk / admin clears it.
  if (current.dnd && isHkMove && action.type !== 'PAUSE_CLEAN') {
    if (options?.isFrontDeskOrAdmin && options?.clearDnd) {
      next.dnd = false
    } else {
      throw new Error('Housekeeping actions are blocked while Do Not Disturb (DND) is active.')
    }
  }

  // Rule: Housekeeping actions cannot start or progress while room is OUT_OF_ORDER or OUT_OF_SERVICE
  if (current.service !== 'IN_SERVICE' && isHkMove) {
    throw new Error(
      `Cannot perform housekeeping action while room is ${current.service.toLowerCase().replace(/_/g, ' ')}.`,
    )
  }

  switch (action.type) {
    case 'CHECK_OUT': {
      next.occupancy = 'VACANT'
      next.hk = 'DIRTY'
      next.dueOutAt = null
      next.dnd = false
      next.cleanRecordId = null
      next.attendantUid = null
      next.startedAt = null
      next.cleanedAt = null
      next.inspectedBy = null
      next.failedItems = []
      next.pausedAt = null
      next.pausedMs = 0
      next.pauseReason = null
      next.startGeo = null
      break
    }

    case 'MARK_OCCUPIED': {
      next.occupancy = 'OCCUPIED'
      next.dueOutAt = null
      break
    }

    case 'SET_DUE_OUT': {
      if (typeof action.dueOutAt !== 'number' || isNaN(action.dueOutAt) || action.dueOutAt <= 0) {
        throw new Error('dueOutAt must be a positive timestamp.')
      }
      next.occupancy = 'DUE_OUT'
      next.dueOutAt = action.dueOutAt
      break
    }

    case 'START_CLEAN': {
      if (current.hk !== 'DIRTY') {
        throw new Error(`Room must be in DIRTY state to start cleaning (current state: ${current.hk}).`)
      }
      if (!action.attendantUid || !action.attendantUid.trim()) {
        throw new Error('attendantUid is required to start cleaning.')
      }

      // Q65 rule: when assignedDay is today and assignedUid is set, only assigned attendant or front desk/admin/supervisor may start
      const trimmedAttendantUid = action.attendantUid.trim()
      const isAssignedToday = Boolean(
        current.assignedUid &&
        current.assignedDay &&
        options?.todayKey &&
        current.assignedDay === options.todayKey,
      )
      if (isAssignedToday) {
        const isAssignedAttendant = current.assignedUid === trimmedAttendantUid
        if (!isAssignedAttendant && !isSupervisor) {
          throw new Error(
            'Room is assigned to another attendant for today. Only assigned attendant or front desk/supervisor may start cleaning.',
          )
        }
        // Front desk or supervisor who starts someone else's room reassigns it in the same write
        if (!isAssignedAttendant && isSupervisor) {
          next.assignedUid = trimmedAttendantUid
        }
      }

      next.hk = 'CLEANING'
      next.attendantUid = trimmedAttendantUid
      next.startedAt = action.startedAt ?? options?.now ?? Date.now()
      next.pausedAt = null
      next.pausedMs = 0
      next.pauseReason = null
      next.startGeo = action.geo ?? null
      break
    }

    case 'PAUSE_CLEAN': {
      if (current.hk !== 'CLEANING') {
        throw new Error(`Room must be in CLEANING state to pause cleaning (current state: ${current.hk}).`)
      }
      if (currentPausedAt !== null) {
        throw new Error('Cleaning is already paused.')
      }
      if (!action.reason || !OPS_ROOM_PAUSE_REASONS.includes(action.reason)) {
        throw new Error('A valid pause reason (GUEST_IN_ROOM, DND, or OTHER) is required.')
      }
      next.pausedAt = action.at ?? options?.now ?? Date.now()
      next.pauseReason = action.reason
      break
    }

    case 'RESUME_CLEAN': {
      if (current.hk !== 'CLEANING') {
        throw new Error(`Room must be in CLEANING state to resume cleaning (current state: ${current.hk}).`)
      }
      if (currentPausedAt === null) {
        throw new Error('Cleaning is not currently paused.')
      }
      const now = action.at ?? options?.now ?? Date.now()
      const delta = Math.max(0, now - currentPausedAt)
      next.pausedMs = currentPausedMs + delta
      next.pausedAt = null
      next.pauseReason = null
      break
    }

    case 'CLEANED': {
      if (current.hk !== 'CLEANING') {
        throw new Error(
          `Room must be in CLEANING state to be marked cleaned (current state: ${current.hk}).`,
        )
      }
      if (!action.cleanRecordId || !action.cleanRecordId.trim()) {
        throw new Error('cleanRecordId is required when room is cleaned.')
      }
      const now = action.cleanedAt ?? options?.now ?? Date.now()
      if (currentPausedAt !== null) {
        const delta = Math.max(0, now - currentPausedAt)
        next.pausedMs = currentPausedMs + delta
        next.pausedAt = null
        next.pauseReason = null
      }
      next.hk = 'CLEANED'
      next.cleanRecordId = action.cleanRecordId.trim()
      next.cleanedAt = now
      next.failedItems = []
      if (options?.reassignAttendantTo && options.reassignAttendantTo.trim()) {
        next.attendantUid = options.reassignAttendantTo.trim()
      }
      break
    }

    case 'INSPECT_PASS': {
      if (current.hk !== 'CLEANED') {
        throw new Error(`Only a CLEANED room can be inspected (current state: ${current.hk}).`)
      }
      if (!action.inspectedBy || !action.inspectedBy.trim()) {
        throw new Error('inspectedBy is required to pass inspection.')
      }
      next.hk = 'READY'
      next.inspectedBy = action.inspectedBy.trim()
      next.failedItems = []
      break
    }

    case 'INSPECT_FAIL': {
      if (current.hk !== 'CLEANED') {
        throw new Error(`Only a CLEANED room can be inspected (current state: ${current.hk}).`)
      }
      if (!action.inspectedBy || !action.inspectedBy.trim()) {
        throw new Error('inspectedBy is required on inspection failure.')
      }
      if (!Array.isArray(action.failedItems) || action.failedItems.length === 0) {
        throw new Error('At least one failed item must be specified on inspection failure.')
      }
      const cleanedFailedItems = action.failedItems.map((item) => String(item).trim()).filter(Boolean)
      if (cleanedFailedItems.length === 0) {
        throw new Error('Failed items cannot be empty strings.')
      }
      next.hk = 'DIRTY'
      next.inspectedBy = action.inspectedBy.trim()
      next.failedItems = cleanedFailedItems
      // attendantUid is kept so the room goes back to the same attendant
      break
    }

    case 'SET_SERVICE': {
      if (action.service !== 'OUT_OF_ORDER' && action.service !== 'OUT_OF_SERVICE') {
        throw new Error(`Invalid service state: ${action.service}.`)
      }
      if (!action.serviceReason || !action.serviceReason.trim()) {
        throw new Error('A reason is required when placing a room out of order or out of service.')
      }
      next.service = action.service
      next.serviceReason = action.serviceReason.trim()
      next.workOrderRequestId = action.workOrderRequestId ? action.workOrderRequestId.trim() : null
      break
    }

    case 'BACK_IN_SERVICE': {
      if (current.service === 'IN_SERVICE') {
        throw new Error('Room is already in service.')
      }
      next.service = 'IN_SERVICE'
      next.serviceReason = null
      next.workOrderRequestId = null
      // Back in service returns the room to DIRTY housekeeping state
      next.hk = 'DIRTY'
      break
    }

    case 'SET_TAGS': {
      if (action.dnd !== undefined) {
        next.dnd = Boolean(action.dnd)
      }
      if (action.vip !== undefined) {
        next.vip = Boolean(action.vip)
      }
      break
    }

    case 'ASSIGN': {
      if (!isSupervisor) {
        throw new Error('Only front desk, supervisor or admin may assign rooms.')
      }
      if (!action.uid || !action.uid.trim()) {
        throw new Error('uid is required to assign room.')
      }
      if (!action.day || !action.day.trim()) {
        throw new Error('day is required to assign room.')
      }
      next.assignedUid = action.uid.trim()
      next.assignedDay = action.day.trim()
      break
    }

    case 'UNASSIGN': {
      if (!isSupervisor) {
        throw new Error('Only front desk, supervisor or admin may unassign rooms.')
      }
      next.assignedUid = null
      next.assignedDay = null
      break
    }

    case 'REASSIGN': {
      if (!isSupervisor) {
        throw new Error('Only front desk, supervisor or admin may reassign rooms.')
      }
      if (!action.uid || !action.uid.trim()) {
        throw new Error('uid is required to reassign room.')
      }
      if (!action.day || !action.day.trim()) {
        throw new Error('day is required to reassign room.')
      }
      next.assignedUid = action.uid.trim()
      next.assignedDay = action.day.trim()
      break
    }

    default: {
      const exhaustiveCheck: never = action
      throw new Error(`Unhandled action type: ${(exhaustiveCheck as { type: string }).type}`)
    }
  }

  const to: OpsRoomEventStateSnapshot = {
    occupancy: next.occupancy,
    hk: next.hk,
    service: next.service,
    dnd: next.dnd,
    vip: next.vip,
  }

  return { nextState: next, from, to }
}

// ---------------------------------------------------------------------------
// Pure Allowed Room Actions Helper
// ---------------------------------------------------------------------------

export interface AllowedRoomActionsCaller {
  uid: string
  isFrontDeskOrAdmin: boolean
  isInspector: boolean
  isSupervisor?: boolean
  hasPropertyAccess: boolean
  todayKey?: string
}

export type AllowedRoomActionType =
  | 'CHECK_OUT'
  | 'MARK_OCCUPIED'
  | 'SET_DUE_OUT'
  | 'START_CLEAN'
  | 'PAUSE_CLEAN'
  | 'RESUME_CLEAN'
  | 'CLEANED'
  | 'INSPECT_PASS'
  | 'INSPECT_FAIL'
  | 'SET_SERVICE'
  | 'BACK_IN_SERVICE'
  | 'SET_TAGS'
  | 'ASSIGN'
  | 'UNASSIGN'
  | 'REASSIGN'

/**
 * Pure helper determining permitted room actions for a given user context.
 * Shared across server QR scan context, client board sheet, and testing.
 */
export function allowedRoomActions(
  state: OpsRoomState,
  caller: AllowedRoomActionsCaller,
): AllowedRoomActionType[] {
  if (!caller.hasPropertyAccess) {
    return []
  }

  const isSupervisor = Boolean(
    caller.isSupervisor !== undefined
      ? caller.isSupervisor
      : caller.isFrontDeskOrAdmin || caller.isInspector,
  )

  const actions: AllowedRoomActionType[] = []

  // Check Out: front desk/admin or any member with property access
  if (state.occupancy !== 'VACANT') {
    actions.push('CHECK_OUT')
  }

  // Front desk / Admin desk actions
  if (caller.isFrontDeskOrAdmin) {
    actions.push('SET_TAGS')
    if (state.occupancy === 'VACANT') {
      actions.push('MARK_OCCUPIED')
      actions.push('SET_DUE_OUT')
    }
    if (state.service === 'IN_SERVICE') {
      actions.push('SET_SERVICE')
    } else {
      actions.push('BACK_IN_SERVICE')
    }
  }

  // Supervisor assignment actions (admin, front desk, or inspector)
  if (isSupervisor) {
    actions.push('ASSIGN')
    if (state.assignedUid) {
      actions.push('UNASSIGN', 'REASSIGN')
    }
  }

  // Housekeeping actions: blocked if room is OUT_OF_ORDER or OUT_OF_SERVICE
  if (state.service === 'IN_SERVICE') {
    // START_CLEAN: room is DIRTY, and (DND is off OR caller is front desk/admin who can clear DND)
    // Q65 rule: if assigned today, only assigned attendant or front desk/supervisor can start
    if (state.hk === 'DIRTY' && (!state.dnd || caller.isFrontDeskOrAdmin)) {
      const isAssignedToday = Boolean(
        state.assignedUid &&
        state.assignedDay &&
        caller.todayKey &&
        state.assignedDay === caller.todayKey,
      )
      if (!isAssignedToday || state.assignedUid === caller.uid || isSupervisor) {
        actions.push('START_CLEAN')
      }
    }

    const isAttendantOrDesk = state.attendantUid === caller.uid || caller.isFrontDeskOrAdmin
    const canDoHk = !state.dnd || caller.isFrontDeskOrAdmin

    // PAUSE_CLEAN: room is CLEANING, not paused, and caller is attendant or front desk/admin (allowed even if DND is on, SO-D205)
    if (state.hk === 'CLEANING' && !state.pausedAt && isAttendantOrDesk) {
      actions.push('PAUSE_CLEAN')
    }

    // RESUME_CLEAN: room is CLEANING, currently paused, and caller is attendant or front desk/admin
    if (state.hk === 'CLEANING' && Boolean(state.pausedAt) && canDoHk && isAttendantOrDesk) {
      actions.push('RESUME_CLEAN')
    }

    // CLEANED: room is CLEANING, and (DND is off OR caller is front desk/admin),
    // AND caller is either the assigned attendant OR front desk/admin
    if (
      state.hk === 'CLEANING' &&
      canDoHk &&
      isAttendantOrDesk
    ) {
      actions.push('CLEANED')
    }

    // Inspection: room is CLEANED, caller is inspector,
    // AND inspector cannot be the attendant who cleaned
    if (state.hk === 'CLEANED' && caller.isInspector) {
      if (!state.attendantUid || state.attendantUid !== caller.uid) {
        actions.push('INSPECT_PASS')
        actions.push('INSPECT_FAIL')
      }
    }
  }

  return actions
}

// ---------------------------------------------------------------------------
// Callable Input and Result Payload Types
// ---------------------------------------------------------------------------

export interface PrepareRoomCleanRecordInput {
  workspaceId: string
  roomId: string
  opId?: string
}

export interface PrepareRoomCleanRecordResult {
  success: boolean
  recordId: string
  templateId: string
  templateVersion?: number
  fields: Array<{ key: string; label: string }>
  photoFields?: Array<{ key: string; label: string }>
}

export interface CheckOutRoomInput {
  workspaceId: string
  roomId: string
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface MarkRoomOccupiedInput {
  workspaceId: string
  roomId: string
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface SetRoomDueOutInput {
  workspaceId: string
  roomId: string
  dueOutAt: number
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface StartRoomCleanInput {
  workspaceId: string
  roomId: string
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
  geo?: OpsRoomGeoPoint | null
}

export interface MarkRoomCleanedInput {
  workspaceId: string
  roomId: string
  values: Record<string, unknown>
  recordId?: string
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
  geo?: OpsRoomGeoPoint | null
}

export interface PauseRoomCleanInput {
  workspaceId: string
  roomId: string
  reason: OpsRoomPauseReason
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface ResumeRoomCleanInput {
  workspaceId: string
  roomId: string
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface StaysHousekeepingReportInput {
  workspaceId: string
  propertyId: string
  fromDay: string
  toDay: string
  department?: string
}

export interface StaysHousekeepingReportRow {
  attendantUid: string
  attendantName: string
  roomsCleaned: number
  roomsPerDay: number
  medianActiveMinutes: number
  avgActiveMinutes: number
  totalPausedMinutes: number
  inspectionFails: number
}

export interface StaysHousekeepingReportResult {
  propertyId: string
  fromDay: string
  toDay: string
  rows: StaysHousekeepingReportRow[]
  isSupervisorView: boolean
}

export interface AssignRoomsInput {
  workspaceId: string
  propertyId: string
  roomIds: string[]
  attendantUid: string
  day?: string
  expectedVersions?: Record<string, number>
  opId?: string
}

export interface UnassignRoomInput {
  workspaceId: string
  roomId: string
  expectedVersion?: number
  opId?: string
}

export interface ReassignRoomInput {
  workspaceId: string
  roomId: string
  attendantUid: string
  day?: string
  expectedVersion?: number
  opId?: string
}

export interface InspectRoomInput {
  workspaceId: string
  roomId: string
  action?: 'PASS' | 'FAIL'
  passed?: boolean
  failedItems?: string[]
  selfInspectionReason?: string | null
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface SetRoomServiceInput {
  workspaceId: string
  roomId: string
  service: 'IN_SERVICE' | 'OUT_OF_ORDER' | 'OUT_OF_SERVICE'
  serviceReason?: string
  workOrderRequestId?: string | null
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface SetRoomTagsInput {
  workspaceId: string
  roomId: string
  dnd?: boolean
  vip?: boolean
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
}

export interface TransitionRoomStateInput {
  workspaceId: string
  roomId: string
  action: OpsRoomAction
  expectedVersion?: number
  opId?: string
  source?: OpsRoomSource
  selfInspectionReason?: string | null
  isInternalInvocation?: boolean
  reassignAttendantTo?: string
  doneGeo?: OpsRoomGeoPoint | null
}

export interface TransitionRoomStateResultPayload {
  success: boolean
  roomState: OpsRoomState
  version: number
  replayed?: boolean
}

